import { getBullkThunk } from '@redux/features/bullk.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import UpdateBullkStatusModal from './UpdateBullkStatusModal';

const Bullk: FC = () => {
    const [page, setPage] = useState<number>(1);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [selectedBullkId, setSelectedBullkId] = useState<number | null>(null);

    // -------------------------------------------------------------
    const dispatch = useAppDispatch();
    // -------------------------------------------------------------
    const { bullkRequests, loading } = useAppSelector((state: RootState) => (state as any).bullkState);
    // -------------------------------------------------------------
    const changePage = (p: string) => {
        setPage(Number(p));
    };

    // -------------------------------------------------------------
    useEffect(() => {
        dispatch(getBullkThunk({ pageSize: 10, pageNumber: page - 1 }));
    }, [page, dispatch]);
    // -------------------------------------------------------------

    const handleUpdateBullk = (id: number, status: string) => {
        // Prevent opening modal if status is already Success or failed
        if (status === 'Success' || status === 'failed') {
            return;
        }
        setSelectedBullkId(id);
        setShowUpdateModal(true);
    };

    const handleCloseUpdateModal = () => {
        setShowUpdateModal(false);
        setSelectedBullkId(null);
        // Refresh the list after closing modal (in case a bullk was updated)
        dispatch(getBullkThunk({ pageSize: 10, pageNumber: page - 1 }));
    };

    // -------------------------------------------------------------

    return (
        <>
            <UpdateBullkStatusModal
                show={showUpdateModal}
                handleClose={handleCloseUpdateModal}
                bullkId={selectedBullkId || 0}
            />
            <BreadCrumb pageTitle={`Bullk Requests`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Bullk Requests List</h3>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Item Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">NDC Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Strength</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Frequency</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Notes</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Proposed Unit Price
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Seller Note</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Details</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(bullkRequests as any)?.content?.length ?? 0} animationKey={`bulk-${page}`}>
                                            {(bullkRequests as any)?.content?.map((bullk: any) => (
                                                <AnimatedTableRow
                                                    rowKey={bullk?.id}
                                                    detailRecord={bullk}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{bullk?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.itemName || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.ndcNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.strength || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.quantity || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.frequency || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{bullk?.notes || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-semibold ${
                                                                bullk?.status === 'Pending'
                                                                    ? 'bg-yellow-100 text-yellow-800'
                                                                    : bullk?.status === 'Success'
                                                                      ? 'bg-green-100 text-green-800'
                                                                      : 'bg-red-100 text-red-800'
                                                            }`}
                                                        >
                                                            {bullk?.status || `-`}
                                                        </span>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {bullk?.proposedUnitPrice !== null &&
                                                        bullk?.proposedUnitPrice !== undefined
                                                            ? `$${bullk.proposedUnitPrice.toFixed(2)}`
                                                            : `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{bullk?.sellerNote || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2.5">
                                                            <button
                                                                className={`mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-yellow-600
                                                        bg-white transition-all duration-300 ease-linear ${
                                                            bullk?.status === 'Success' || bullk?.status === 'failed'
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : 'hover:scale-105'
                                                        }`}
                                                                onClick={() => {
                                                                    handleUpdateBullk(bullk?.id, bullk?.status);
                                                                }}
                                                                disabled={
                                                                    bullk?.status === 'Success' ||
                                                                    bullk?.status === 'failed'
                                                                }
                                                            >
                                                                Update Status
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(bullkRequests as any)?.pageable?.pageSize || 20}
                                    total={(bullkRequests as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={
                                        (bullkRequests as any)?.numberOfElements ||
                                        (bullkRequests as any)?.content?.length ||
                                        0
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Bullk;
